import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  formatNumber,
  getTypeByComparison,
  getPriceDataFromOrderbookData,
  checkShouldTableHide,
  calculateSummary,
  isTensOfMillions,
} from 'features/company/Stream/TableOrderbook/utils';
import { useOrderbookSingleReceiver } from 'global/Datafeed/UseOrderbook/useOrderbookSingleReceiver';
import TableEmptyOrderbook from 'features/company/Stream/TableOrderbook/TableEmpty';
import {
  CompanyOrderbook,
  OrderbookPriceData,
} from '../../../../../../@types/orderbook';
import { OrderbookTableWrapper } from './styled';
import TableOrderbookItemSummary from './TableOrderbookItemSummary';
import { setValue } from './store';

interface TableBidOfferProps {
  orderbook: Partial<CompanyOrderbook>;
  symbol: string;
  isBlur: boolean;
}

function TableBidOffer({ orderbook, symbol, isBlur }: Readonly<TableBidOfferProps>) {
  const liveOrderbookData = orderbook;

  const orderbookData: Array<OrderbookPriceData> = useMemo(
    () => getPriceDataFromOrderbookData(liveOrderbookData),
    [liveOrderbookData],
  );
  const { isHideTable, isIHSG } = useMemo(
    () => checkShouldTableHide(liveOrderbookData),
    [liveOrderbookData],
  );
  const { previous } = liveOrderbookData;

  const getColor = useCallback((data, dataToCompare): string => {
    if (data === 0) return undefined;

    return getTypeByComparison(data, dataToCompare);
  }, []);

  const reformatColumnData = useCallback((data: string | number): string => {
    if (data === 0) return '-';

    return formatNumber(data);
  }, []);

  const getClassName = useCallback(
    (index) => {
      if (index === 0) {
        return ' first';
      } if (index === orderbookData.length - 1) {
        return ' last';
      }
        return '';
    },
    [orderbookData],
  );

  const rowClassName = isBlur ? 'blurred' : '';

  const getLotClassName = useCallback((value: string | number, isFormatted = false) => {
    const formattedValue = isFormatted ? String(value) : reformatColumnData(value);
    return isTensOfMillions(formattedValue) ? ' lot' : '';
  }, []);

  const renderItemOrderbook = useCallback((data: Array<OrderbookPriceData>) => data.map(({ bidLot, bidPrice, bidFrequency, offerPrice, offerLot, offerFrequency }, index) => (
    <tr key={`item-orderbook-${uuidv4()}`} className={rowClassName}>
      <td key={`bid-frequency-${index}`} className={`orderbook-text-cell value frequency${getClassName(index)}`}>
        {reformatColumnData(bidFrequency)}
      </td>
      <td key={`bid-lot-${index}`} className={`orderbook-text-cell value${getClassName(index)}${getLotClassName(bidLot)}`}>
        {reformatColumnData(bidLot)}
      </td>
      <td key={`bid-price-${index}`} className={`orderbook-text-cell value${getClassName(index)} ${getColor(bidPrice, previous)}`}>
        {reformatColumnData(bidPrice)}
      </td>
      <td key={`offer-price-${index}`} className={`orderbook-text-cell value${index === 0 ? ' first' : ''}${index === orderbookData.length - 1 ? ' last' : ''} ${getColor(offerPrice, previous)}`}>
        {reformatColumnData(offerPrice)}
      </td>
      <td key={`offer-lot-${index}`} className={`orderbook-text-cell value${getClassName(index)}${getLotClassName(bidLot)}`}>
        {reformatColumnData(offerLot)}
      </td>
      <td key={`offer-frequency-${index}`} className={`orderbook-text-cell value frequency${getClassName(index)}`}>
        {reformatColumnData(offerFrequency)}
      </td>
    </tr>
    )), [previous, orderbookData, isBlur]);

  const renderOrderbookSummary = useCallback((data: Array<OrderbookPriceData>) => {
    const totalLots = useMemo(() => calculateSummary(data), [data]);
    const totalBid = useMemo(() => totalLots.bid, [totalLots.bid]);
    const totalOffer = useMemo(() => totalLots.offer, [totalLots.offer]);
    const totalBidFrequency = useMemo(() => formatNumber(totalLots.bidFrequency), [totalLots.bidFrequency]);
    const totalOfferFrequency = useMemo(() => formatNumber(totalLots.offerFrequency), [totalLots.offerFrequency]);

    return (
      <tr key="summary" className={rowClassName}>
        <td key="bid-frequency-total" className="orderbook-text-summary">{totalBidFrequency}</td>
        <td key="bid-total" className={`orderbook-text-summary${getLotClassName(totalBid, true)}`}>{totalBid}</td>
        <td key="total" className="orderbook-text-summary center" colSpan={2}>TOTAL</td>
        <td key="offer-total" className={`orderbook-text-summary${getLotClassName(totalOffer, true)}`}>{totalOffer}</td>
        <td key="offer-frequency-total" className="orderbook-text-summary">{totalOfferFrequency}</td>
      </tr>
    );
  }, [isBlur]);

  const liveOrderbookOnMessage = useCallback((message) => {
    setValue({
      key: 'liveprice',
      symbol: message.stockCode,
      type: 'update_orderbook_pricedata_liveprice',
      value: { ...message, symbol: message.stockCode },
    });
  }, []);
  useOrderbookSingleReceiver(
    symbol,
    'sidebar_orderbook_bid_offer',
    liveOrderbookOnMessage,
  );

  const renderTable = () => {
    if (isHideTable && isBlur) {
      return <TableEmptyOrderbook />;
    }

    if (isHideTable) {
      return <OrderbookTableWrapper $isIHSG={isIHSG} />;
    }

    return (
      <TableOrderbookItemSummary
        data={orderbookData}
        renderItemOrderbook={renderItemOrderbook}
        renderOrderbookSummary={renderOrderbookSummary}
      />
    );
  };

  return <OrderbookTableWrapper>{renderTable()}</OrderbookTableWrapper>;
}

export default TableBidOffer;
